import {
    useEffect,
    useRef,
    useState } from "react";
import { Link,
    useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import { MediaPicker } from "../components/MediaPicker";
import {
    adminCreateClient,
    adminDeleteClient,
    adminListClients,
    adminMe,
    adminReorderClients,
    adminUpdateClient,
} from "../lib/api";
import { AdminHeader } from "../components/AdminNav";

const blank = { name: "", logo: "", website: "", category: "", enabled: true };

export default function AdminClients() {
    const navigate = useNavigate();
    const [clients, setClients] = useState(null);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null); // null | {new: bool, ...client}
    const [busy, setBusy] = useState(false);
    const dragIdRef = useRef(null);

    useEffect(() => {
        adminMe().catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    const load = () =>
        adminListClients()
            .then(setClients)
            .catch((err) =>
                setError(err?.response?.data?.detail || "Could not load clients")
            );

    useEffect(() => {
        load();
    }, []);

    const startNew = () => setEditing({ new: true, ...blank });
    const startEdit = (c) => setEditing({ new: false, ...c });

    const save = async () => {
        setError("");
        setBusy(true);
        try {
            const { new: isNew, id, ...payload } = editing;
            if (isNew) {
                await adminCreateClient(payload);
            } else {
                await adminUpdateClient(id, payload);
            }
            setEditing(null);
            await load();
        } catch (err) {
            setError(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id, name) => {
        if (!window.confirm(`Delete "${name}"?`)) return;
        try {
            setBusy(true);
            await adminDeleteClient(id);
            await load();
        } catch (err) {
            setError(err?.response?.data?.detail || "Delete failed");
        } finally {
            setBusy(false);
        }
    };

    const toggleEnabled = async (c) => {
        try {
            setBusy(true);
            await adminUpdateClient(c.id, {
                name: c.name,
                logo: c.logo,
                website: c.website || "",
                category: c.category || "",
                enabled: !c.enabled,
            });
            await load();
        } catch (err) {
            setError(err?.response?.data?.detail || "Update failed");
        } finally {
            setBusy(false);
        }
    };

    // ---- Drag & drop reorder ----
    const onDragStart = (id) => (e) => {
        dragIdRef.current = id;
        e.dataTransfer.effectAllowed = "move";
    };
    const onDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };
    const onDrop = (targetId) => async (e) => {
        e.preventDefault();
        const sourceId = dragIdRef.current;
        dragIdRef.current = null;
        if (!sourceId || sourceId === targetId || !clients) return;
        const ids = clients.map((c) => c.id);
        const from = ids.indexOf(sourceId);
        const to = ids.indexOf(targetId);
        if (from < 0 || to < 0) return;
        const next = [...ids];
        next.splice(from, 1);
        next.splice(to, 0, sourceId);
        try {
            setBusy(true);
            const updated = await adminReorderClients(next);
            setClients(updated);
        } catch (err) {
            setError(err?.response?.data?.detail || "Reorder failed");
        } finally {
            setBusy(false);
        }
    };

    const linkCls =
        "text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]";

    return (
        <main
            data-testid="page-admin-clients"
            className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16 md:py-24"
        >
            <Seo path="/admin/clients" title="Clients — Admin" description="Manage Trusted By logos" noindex />
            <AdminHeader title="Clients — Trusted By" />
            <div className="mt-8 flex justify-end">
                <button
                    onClick={startNew}
                    data-testid="new-client"
                    className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] bg-[#1A1A1A] text-[#F9F8F6] hover:bg-black"
                >
                    + New client
                </button>
            </div>

            {error && <p className="mt-6 text-red-700 text-sm" data-testid="clients-error">{error}</p>}

            <section className="mt-10">
                <p className="text-sm text-[#5C5855] max-w-2xl mb-6">
                    Drag rows to reorder. Toggle &lsquo;Enabled&rsquo; to control which logos appear in the
                    public &lsquo;Trusted By&rsquo; marquee. Every logo is rendered in the same neutral grey
                    on the public site for optical consistency — upload transparent PNGs.
                </p>

                {clients == null ? (
                    <p className="asc-overline" data-testid="clients-loading">Loading…</p>
                ) : clients.length === 0 ? (
                    <p className="text-[#5C5855]" data-testid="clients-empty">
                        No clients yet — click &lsquo;+ New client&rsquo; to add one.
                    </p>
                ) : (
                    <ul data-testid="clients-list" className="space-y-3">
                        {clients.map((c) => (
                            <li
                                key={c.id}
                                data-testid={`client-row-${c.id}`}
                                draggable
                                onDragStart={onDragStart(c.id)}
                                onDragOver={onDragOver}
                                onDrop={onDrop(c.id)}
                                className={`bg-white border border-[#E5E1DB] p-4 flex items-center gap-4 cursor-move ${
                                    !c.enabled ? "opacity-60" : ""
                                }`}
                            >
                                <span className="text-[#8C7A6B] select-none w-6 text-center" aria-hidden>⇅</span>
                                <div className="w-24 h-14 bg-[#F2EFEB] border border-[#E5E1DB] flex items-center justify-center shrink-0 p-2">
                                    {c.logo ? (
                                        <img
                                            src={c.logo}
                                            alt={c.name}
                                            className="max-w-full max-h-full object-contain"
                                            style={{ filter: "grayscale(1) brightness(0.5)" }}
                                        />
                                    ) : (
                                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#8C7A6B]">No logo</span>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[#1A1A1A] font-medium truncate">{c.name}</p>
                                    <p className="text-xs text-[#8C7A6B] truncate">
                                        {c.category || "—"}
                                        {c.website && <> · <a href={c.website} target="_blank" rel="noreferrer" className="asc-link">{c.website}</a></>}
                                    </p>
                                </div>
                                <label
                                    className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#5C5855]"
                                >
                                    <input
                                        type="checkbox"
                                        data-testid={`toggle-enabled-${c.id}`}
                                        checked={!!c.enabled}
                                        onChange={() => toggleEnabled(c)}
                                    />
                                    Enabled
                                </label>
                                <button
                                    onClick={() => startEdit(c)}
                                    data-testid={`edit-${c.id}`}
                                    className="text-[10px] uppercase tracking-[0.2em] text-[#1A1A1A] hover:underline"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => remove(c.id, c.name)}
                                    data-testid={`delete-${c.id}`}
                                    disabled={busy}
                                    className="text-[10px] uppercase tracking-[0.2em] text-red-700 hover:text-red-900 disabled:opacity-40"
                                >
                                    Delete
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {editing && (
                <div
                    data-testid="client-editor"
                    className="fixed inset-0 z-40 bg-black/40 flex justify-end"
                    onClick={() => setEditing(null)}
                >
                    <aside
                        onClick={(e) => e.stopPropagation()}
                        className="w-full sm:w-[520px] bg-[#F9F8F6] h-full overflow-y-auto"
                    >
                        <header className="flex items-center justify-between border-b border-[#E5E1DB] px-6 py-5">
                            <p className="asc-overline">{editing.new ? "New client" : "Edit client"}</p>
                            <button onClick={() => setEditing(null)} className="text-xs text-[#5C5855]">Close ✕</button>
                        </header>
                        <div className="px-6 py-6 space-y-5">
                            <Field label="Client name" required>
                                <input
                                    data-testid="client-name"
                                    value={editing.name}
                                    onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
                                    className={inputCls}
                                />
                            </Field>
                            <MediaPicker
                                label="Logo (transparent PNG recommended)"
                                testid="client-logo"
                                value={editing.logo}
                                onChange={(url) => setEditing((s) => ({ ...s, logo: url }))}
                            />
                            <Field label="Category" hint="e.g. Hospitality Group / Developer / Operator">
                                <input
                                    data-testid="client-category"
                                    value={editing.category}
                                    onChange={(e) => setEditing((s) => ({ ...s, category: e.target.value }))}
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Website" hint="Optional — full URL">
                                <input
                                    data-testid="client-website"
                                    value={editing.website}
                                    onChange={(e) => setEditing((s) => ({ ...s, website: e.target.value }))}
                                    placeholder="https://…"
                                    className={inputCls}
                                />
                            </Field>
                            <label className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                                <input
                                    type="checkbox"
                                    data-testid="client-enabled"
                                    checked={!!editing.enabled}
                                    onChange={(e) => setEditing((s) => ({ ...s, enabled: e.target.checked }))}
                                />
                                Enabled (show on the public site)
                            </label>
                            <div className="flex items-center gap-6 pt-4 border-t border-[#E5E1DB]">
                                <button
                                    onClick={save}
                                    disabled={busy || !editing.name}
                                    data-testid="save-client"
                                    className="px-6 py-3 bg-[#1A1A1A] text-[#F9F8F6] text-[11px] uppercase tracking-[0.22em] hover:bg-black disabled:opacity-50"
                                >
                                    {busy ? "Saving…" : editing.new ? "Create client" : "Save changes"}
                                </button>
                                <button onClick={() => setEditing(null)} className={linkCls}>Cancel</button>
                            </div>
                        </div>
                    </aside>
                </div>
            )}
        </main>
    );
}

const inputCls = "w-full bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm text-[#1A1A1A]";

const Field = ({ label, hint, required, children }) => (
    <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] mb-2">
            {label}{required && <span className="text-red-700"> *</span>}
            {hint && <span className="ml-2 lowercase tracking-normal text-[#A89F94] normal-case">— {hint}</span>}
        </span>
        {children}
    </label>
);
